import React, { MutableRefObject, useCallback, useMemo } from 'react';
import { UpdateToEventView } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useDexMessageBootstrap } from './use-dex-message-bootstrap';
import { useUpdateToEventState } from './use-update-to-event-state';
import { UpdateToEventButton } from './update-to-event-button';
import { EventAutocomplete, StylesOverride } from './types';
import { getDisabledMessage } from './helpers';

export function DexUpdateToEvent({
    dexMessageId,
    isSummaryView,
    unitAssignedEventId,
    disableInitialBootstrap = false,
    showEntitiesPopoverRef,
    EventAutocompleteComponent,
    stylesOverride = {},
    showLeadingVisualIcon,
    showTrailingVisualIcon,
    onUpdateToEventSuccess,
    linkedEventId,
}: {
    dexMessageId?: number;
    isSummaryView?: boolean;
    unitAssignedEventId?: number | null;
    disableInitialBootstrap?: boolean;
    showEntitiesPopoverRef?: MutableRefObject<(() => void) | undefined>;
    EventAutocompleteComponent?: EventAutocomplete;
    stylesOverride?: StylesOverride;
    showLeadingVisualIcon?: boolean;
    showTrailingVisualIcon?: boolean;
    onUpdateToEventSuccess?: () => void;
    linkedEventId?: number;
}) {
    const enabledFeatures = useEnabledFeatures();
    const isUnassignedUnit = unitAssignedEventId === null;
    const initialEventId = linkedEventId ?? unitAssignedEventId ?? undefined;

    const {
        bootstrapEventId,
        bootstrapActive,
        setEventId,
        enableBootstrap,
        popoverState,
        popoverDispatch,
    } = useUpdateToEventState({ initialEventId, disableInitialBootstrap });

    const { data, isLoading, isError, refetch } = useDexMessageBootstrap({
        dexMessageId,
        eventId: bootstrapEventId,
        isSummaryView,
        enabled: bootstrapActive && !!enabledFeatures?.dexParsingEnabled,
    });

    const updateToEventView: UpdateToEventView | undefined = useMemo(() => {
        const first = data?.updateToEventQueryResults?.[0];
        if (!first) return undefined;
        return {
            showUpdateToEventButton: first.showUpdateToEventButton,
            eventAutocompleteView: data!.eventAutocompleteView,
            eventPersons: first.eventPersons,
            eventVehicles: first.eventVehicles,
            matchingEventPerson: first.matchingEventPerson,
            matchingEventVehicle: first.matchingEventVehicle,
            parsedPerson: first.parsedPerson,
            parsedVehicle: first.parsedVehicle,
            eventNumber: data!.eventAutocompleteView?.agencyEventNumber,
            eventId: data!.eventAutocompleteView?.agencyEventId,
        };
    }, [data]);

    // When disableInitialBootstrap is true, the parent controls visibility externally
    // (e.g. dex-activity-entry gates on dexActivityTabUpdateToEventView?.showUpdateToEvent).
    // The button must render so the user can click it to trigger the first bootstrap.
    const shouldShowButton =
        disableInitialBootstrap ||
        !!updateToEventView?.showUpdateToEventButton ||
        popoverState.isPopoverOpen ||
        popoverState.isConfirmModalOpen;

    const disabledMessage = getDisabledMessage(isError, isUnassignedUnit);

    const handleUpdateSuccess = useCallback(() => {
        refetch();
        onUpdateToEventSuccess?.();
    }, [refetch, onUpdateToEventSuccess]);

    if (!shouldShowButton) {
        return null;
    }

    return (
        <UpdateToEventButton
            updateToEventView={
                (bootstrapEventId || updateToEventView?.eventId) ? updateToEventView : undefined
            }
            setEventId={setEventId}
            isBootstrapLoading={isLoading}
            onUpdateToEventSuccess={handleUpdateSuccess}
            disabledMessage={disabledMessage}
            showEntitiesPopoverRef={showEntitiesPopoverRef}
            EventAutocompleteComponent={EventAutocompleteComponent}
            stylesOverride={stylesOverride}
            onShowEntitiesPopover={enableBootstrap}
            showLeadingVisualIcon={showLeadingVisualIcon}
            showTrailingVisualIcon={showTrailingVisualIcon}
            popoverState={popoverState}
            popoverDispatch={popoverDispatch}
        />
    );
}
